import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import ParticipantBookingPanel from './ParticipantBookingPanel';

/**
 * Custom overlay + dialog — not react-bootstrap's <Modal>. That component's
 * .modal-dialog/.modal-lg classes come from bootstrap.min.css (imported in
 * src/index.js, ahead of every component stylesheet), and repeated attempts
 * to widen/center this modal via .pbt-book-modal-dialog overrides (with and
 * without !important) did not change the rendered size in the field even
 * though an automated, authenticated run of this exact code measured the
 * override taking effect correctly. Rather than keep fighting an
 * unreproducible cascade conflict, this owns its own fixed-overlay +
 * flex-centered layout with no Bootstrap modal classes in play at all.
 */
const BookRoomModal = ({
  show,
  stayIndex,
  totalStays,
  stayInKey,
  stayOutKey,
  competitionId,
  participantId,
  participantName,
  existingSegments,
  guests,
  onDone,
  onHide,
}) => {
  useEffect(() => {
    if (!show) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onHide();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [show, onHide]);

  if (!show) return null;

  // ParticipantBookingPanel exposes a single `onDone` callback, fired both
  // when its "Close" button is clicked and after a successful booking — it
  // cannot distinguish the two (and per Global Constraints its internals are
  // unchanged). Route both through here: always dismiss the modal, and also
  // let the parent refresh its booking data.
  const handlePanelDone = () => {
    onDone();
    onHide();
  };

  return createPortal(
    <div
      className="pbt-book-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onHide();
      }}
    >
      <div className="pbt-book-modal-dialog pbt-book-modal-dialog--picker" role="dialog" aria-modal="true">
        <div className="pbt-book-modal__head">
          <div className="pbt-book-modal__head-main">
            <div className="pbt-book-modal__titlerow">
              <h4 className="pbt-book-modal__title">Book a Room</h4>
              {totalStays > 1 && (
                <span className="pbt-book-modal__stay-chip">Stay {stayIndex + 1} of {totalStays}</span>
              )}
            </div>
          </div>
          <button type="button" className="pbt-book-modal__close-x" aria-label="Close dialog" onClick={onHide}>
            <X size={18} />
          </button>
        </div>

        <ParticipantBookingPanel
          mode="create"
          stayInKey={stayInKey}
          stayOutKey={stayOutKey}
          competitionId={competitionId}
          participantId={participantId}
          participantName={participantName}
          existingSegments={existingSegments}
          guests={guests}
          onDone={handlePanelDone}
        />
      </div>
    </div>,
    document.body
  );
};

export default BookRoomModal;
